import { Inject, Injectable } from '@angular/core';
import {
  AuthorizationActions,
  ChangeAuthorizationCommand,
  USER_GROUP_ACTIONS,
  UserGroupActions
} from '@/app/platform/platform-data/actions/types';
import { ErrorInformation } from '@shared/models/error/error-information';
import { FeatureType, PageFeature } from '@ervo/page-framework';
import { err } from '@common/result/Result';
import { allSucceeded, extractErrors, hasError } from '@common/result/result-utils';
import { USER_GROUP_SERVICE, UserGroupService } from '@/app/platform/platform-data/services/types';
import { AsyncResult } from '@common/result/asyncResult';
import { UserGroupEntityStore } from '@/app/platform/platform-data/store/user-group.entity.store';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';

@Injectable({
  providedIn: 'root'
})
export class GroupAuthorizationStoreActions implements AuthorizationActions {
  constructor(
    @Inject(USER_GROUP_ACTIONS)
    private readonly userGroupActions: UserGroupActions,
    private readonly userGroupStore: UserGroupEntityStore,
    @Inject(USER_GROUP_SERVICE)
    private readonly userGroupService: UserGroupService
  ) {}

  async linkAppToGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.linkAppToUserGroup(userGroup, app);
    result.ifSuccess(group => {
      this.userGroupActions.loadLinkedApps(group);
      this.userGroupStore.update(group.id, group);
    });
    return result;
  }

  async unlinkAppFromGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity> {
    const result = await this.userGroupService.unlinkAppFromUserGroup(userGroup, app);
    result.ifSuccess(group => {
      this.userGroupStore.removeAppLinkForGroup(userGroup, app);
      this.userGroupStore.update(group.id, group);
    });

    return result;
  }

  private async authorizeNavigationItem(
    navigationItem: NavigationItemEntity,
    group: UserGroupEntity
  ): AsyncResult<any> {
    if (navigationItem.isClickTroughPage) {
      const result = await this.authorizeClickThroughFeatureInParent(navigationItem, group);
      if (result.hasFailed) {
        return result;
      }
    }

    return await this.userGroupService.authorizeNavigationItem(group, navigationItem);
  }

  private async authorizeClickThroughFeatureInParent(
    navigationItem: NavigationItemEntity,
    group: UserGroupEntity
  ): AsyncResult<any> {
    return await this.userGroupService.authorizePageFeature(group, navigationItem.parentId, {
      name: navigationItem.name,
      pageId: navigationItem.parentId,
      featureId: navigationItem.pageComponentId,
      type: FeatureType.Regular
    });
  }

  private async unAuthorizeNavigationItem(
    navigationItem: NavigationItemEntity,
    group: UserGroupEntity
  ): AsyncResult<any> {
    if (navigationItem.isClickTroughPage) {
      const result = await this.unAuthorizeClickThrougFeatureInParent(navigationItem, group);
      if (result.hasFailed) {
        return result;
      }
    }
    return await this.userGroupService.unAuthorizeNavigationItem(group, navigationItem);
  }

  private async unAuthorizeClickThrougFeatureInParent(
    navigationItem: NavigationItemEntity,
    group: UserGroupEntity
  ): AsyncResult<any> {
    const { parentId, id } = navigationItem;
    const parentFeature = group.authorizations.find(
      a => a.navigationItemId === parentId && a.featureId === navigationItem.pageComponentId
    );

    if (!parentFeature) {
      throw new Error('Cannot find feature on parent!');
    }

    return await this.userGroupService.unAuthorizePageFeature(parentFeature);
  }

  async changeFeatureAuthorizations(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<PageFeature, NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity> {
    const promises = this.makeFeatureAuthorizationRequests(group, commands);
    const results = await Promise.all(promises);
    if (hasError(results)) {
      return err(extractErrors(results));
    }

    return await this.userGroupActions.reloadOne(group.id);
  }

  async changeNavigationItemAuthorizations(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity> {
    const promises = this.makeNavigationItemAuthorizationRequests(group, commands);

    const results = await Promise.all(promises);

    if (allSucceeded(results)) {
      return this.userGroupActions.reloadOne(group.id);
    }

    const errors = extractErrors(results);
    return err(errors);
  }

  async changeAuthorizations(
    group: UserGroupEntity,
    navigationItemCommands: ChangeAuthorizationCommand<NavigationItemEntity>[],
    featureCommands: ChangeAuthorizationCommand<PageFeature, NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity> {
    const navigationItemRequests = this.makeNavigationItemAuthorizationRequests(
      group,
      navigationItemCommands
    );
    const featureRequests = this.makeFeatureAuthorizationRequests(group, featureCommands);

    const results = await Promise.all([...navigationItemRequests, ...featureRequests]);

    if (hasError(results)) {
      return err(extractErrors(results));
    }
    return this.userGroupActions.reloadOne(group.id);
  }

  private makeNavigationItemAuthorizationRequests(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<NavigationItemEntity>[]
  ): AsyncResult<any>[] {
    return commands.map(async ({ type, item }) => {
      if (type === 'authorize') {
        return this.authorizeNavigationItem(item, group);
      }

      return this.unAuthorizeNavigationItem(item, group);
    });
  }

  private makeFeatureAuthorizationRequests(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<PageFeature, NavigationItemEntity>[]
  ): AsyncResult<any>[] {
    return commands.map(cmd => {
      if (cmd.type === 'authorize') {
        return this.userGroupService.authorizePageFeature(group, cmd.extraItem.id, cmd.item);
      }

      const feature = cmd.item;
      const navigationItem = cmd.extraItem;

      const authorization = group.authorizations.find(
        a => a.navigationItemId === navigationItem.id && a.featureId === feature.featureId
      );

      return this.userGroupService.unAuthorizePageFeature(authorization);
    });
  }
}
